<script lang="ts" setup>
import { ref } from 'vue';
import { useLocalization } from '@/plugins/localization';
import properties, { FieldTextarea } from '@/components/forms/properties';
import AiFormGeneratorService, { GeneratedFormResponse } from '@/modules/logito/ai/form-generator/services/AiFormGeneratorService';
import { FormBuilderContract } from '@/components/builder/form';
import IdeoTooltip from '@/components/ideo/tooltip/IdeoTooltip.vue';

defineOptions({
    name: 'AiFormGenerator',
    components: {
        ...properties,
        FieldTextarea,
        IdeoTooltip
    }
});

const { $t } = useLocalization();

// Mapa typów obsługiwanych pól
const TYPE_LABELS: Record<string, string> = {
    'text': '[[[Tekst]]]',
    'textarea': '[[[Tekst wieloliniowy]]]',
    'email': '[[[Email]]]',
    'numeric': '[[[Liczba]]]',
    'date': '[[[Data]]]',
    'boolean': '[[[Tak/Nie]]]',
    'choice': '[[[Wybór]]]',
    'attachments': '[[[Załączniki]]]',
    'company': '[[[Spółka]]]',
    'register': '[[[Rejestr]]]',
    'user': '[[[Użytkownik]]]',
    'contractor': '[[[Kontrahent]]]',
    'usergroup': '[[[Grupa użytkowników]]]',
    'document': '[[[Dokument]]]',
    'exchangerates': '[[[Kursy walut]]]',
    'dictionary': '[[[Słownik Logito]]]',
    'relateddocument': '[[[Dokument powiązany]]]',
    'vattable': '[[[Tabela VAT]]]',
    'table': '[[[Tabela]]]'
};

// Props
const props = defineProps({
  "formBuilder": null
});

const userPrompt = ref('');
const isLoading = ref(false);
const error = ref('');
const result = ref<GeneratedFormResponse | null>(null);

const generateForm = async (): Promise<void> =>
{
    if (!userPrompt.value.trim())
    {
        return;
    }

    isLoading.value = true;
    error.value = '';
    result.value = null;

    try
    {
        const response = await AiFormGeneratorService.generateForm(userPrompt.value.trim());

        result.value = response;
    }
    catch (err: any)
    {
        error.value = err.message || $t('[[[Wystąpił błąd podczas generowania formularza]]]');
    }
    finally
    {
        isLoading.value = false;
    }
};

const getComponentTypeLabel = (aiComponent: any): string =>
{
    const displayType = aiComponent.originalType || aiComponent.type;
    const label = TYPE_LABELS[displayType];

    return label ? $t(label) : displayType;
};

const getAvailableFieldsTooltip = (): string =>
{
    const allFields = Object.keys(TYPE_LABELS).map(type => getComponentTypeLabel({ originalType: type }));

    return `${$t('[[[Obsługiwane typy pól:]]]')}\n- ${allFields.join('\n- ')}`;
};

const setupChoiceOptions = (component: any, options: string[]): void =>
{
    component.options = options.map((opt: string, optIndex: number) => ({
        value: (optIndex + 1).toString(),
        text: { 'pl-PL': opt },
        selected: false,
        key: (optIndex + 1).toString()
    }));
    component.fieldType = 'Dropdown';
};


const addComponentsToForm = (components: any[]): void =>
{
    try
    {
        const form = props.formBuilder.blueprint;
        let targetPage = form.components.find((comp: any) => comp.type === 'page');

        if (!targetPage)
        {
            targetPage = props.formBuilder.layout.addPage(form);
        }

        if (!targetPage)
        {
            console.error('Nie można znaleźć/utworzyć strony docelowej w formularzu');

            return;
        }

        components.forEach((aiComponent, index) =>
        {
            try
            {
                let row = targetPage.components.find((comp: any) => comp.type === 'row' && comp.components.length === 0);

                if (!row)
                {
                    row = props.formBuilder.layout.addRow(targetPage);
                }

                const component = props.formBuilder.layout.createComponent(aiComponent.type);

                component.width = 3;

                if (component && aiComponent.aiLabel)
                {
                    component.label = { 'pl-PL': aiComponent.aiLabel };
                }

                if (component && typeof aiComponent.aiRequired === 'boolean')
                {
                    component.required = aiComponent.aiRequired ? 'Always' : 'Never';
                }

                if (component && aiComponent.aiPlaceholder)
                {
                    component.placeholder = { 'pl-PL': aiComponent.aiPlaceholder };
                }

                // Specjalne przypadki
                if (aiComponent.type === 'text' && aiComponent.originalType === 'email')
                {
                    // Ustawienia dla pola email
                    component.validation = 'RegularExpression';
                    component.validationRule = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$';
                    component.validationMessage = { 'pl-PL': 'Podaj poprawny adres email' };
                    component.characters = { min: 0, max: 100 };
                    component.rows = 1;
                }
                else if (aiComponent.type === 'text' && aiComponent.originalType === 'textarea')
                {
                    // Ustawienia dla pola textarea
                    component.rows = 4;
                    component.fieldType = 'Multiline';
                    component.characters = { min: 0, max: 500 };
                }

                if (aiComponent.type === 'logito-choice' && aiComponent.aiOptions?.length > 0)
                {
                    setupChoiceOptions(component, aiComponent.aiOptions);
                }

                // Obsługa table z kolumnami
                if (aiComponent.type === 'table' && aiComponent.aiColumns?.length > 0)
                {
                    component.components = [];

                    const existingNames = props.formBuilder.schema.names();
                    const createdNames: string[] = [];

                    aiComponent.aiColumns.forEach((column: any) =>
                    {
                        const columnType = column.type;
                        const allNames = [...existingNames, ...createdNames];
                        const uniqueName = props.formBuilder.schema.name(columnType, allNames);

                        createdNames.push(uniqueName);

                        const columnComponent = props.formBuilder.layout.createComponent(columnType);

                        columnComponent.name = uniqueName;
                        columnComponent.label = { 'pl-PL': column.label };


                        if (column.type === 'logito-choice' && column.options)
                        {
                            setupChoiceOptions(columnComponent, column.options);
                        }

                        component.components.push(columnComponent);
                    });

                    component.columns = aiComponent.aiColumns.map((column: any) => ({
                        columnHeader: { 'pl-PL': column.label },
                        columnType: {
                            type: column.type,
                            name: column.label,
                            group: 'primary'
                        }
                    }));

                    component.numberOfRows = 1;
                    component.canAddRow = true;
                }

                props.formBuilder.layout.insertComponent(row, component);

            }
            catch (componentError)
            {
                console.error(`Błąd podczas tworzenia komponentu ${aiComponent.aiLabel}:`, componentError);
            }
        });

    }
    catch (error)
    {
        console.error('Błąd podczas dodawania komponentów:', error);
        error.value = $t('[[[Nie udało się dodać komponentów do formularza]]]');
    }
};
</script>

<template>
    <div class="ai-form-generator">
        <h5>
            {{ $t('[[[Generator AI]]]') }}
            <IdeoTooltip :tooltip="getAvailableFieldsTooltip()" position="left" :multiline="true">
                <i class="fas fa-info-circle text-muted ms-2" style="font-size: 0.9em; cursor: help;"></i>
            </IdeoTooltip>
        </h5>
        <div class="mb-3">
            <FieldTextarea
                v-model="userPrompt"
                :label="$t('[[[Opisz formularz, który chcesz wygenerować]]]')"
                :placeholder="$t('[[[Wprowadź opis formularza, np. formularz kontaktowy z polami: imię, nazwisko, email, telefon, wiadomość]]]')"
                :rows="4"
            />
        </div>
        <div class="d-grid">
            <button
                type="button"
                class="btn btn-primary"
                :disabled="isLoading || !userPrompt.trim()"
                @click="generateForm"
            >
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isLoading ? $t('[[[Generowanie...]]]') : $t('[[[Generuj formularz]]]') }}
            </button>
        </div>
        <div v-if="error" class="alert alert-danger mt-3" role="alert">
            {{ error }}
        </div>
        <div v-if="result?.success" class="alert alert-success mt-3" role="alert">
            <strong>{{ $t('[[[Formularz został wygenerowany pomyślnie!]]]') }}</strong>
            <div v-if="result.formData" class="mt-2">
                <small class="text-muted">
                    {{ $t('[[[Tytuł:]]]') }} {{ result.formData.title }}<br>
                    {{ $t('[[[Opis:]]]') }} {{ result.formData.description }}<br>
                    {{ $t('[[[Ilość pól:]]]') }} {{ result.formData.components?.length || 0 }}
                </small>

                <!-- Preview wygenerowanych komponentów -->
                <div class="mt-3">
                    <h6>{{ $t('[[[Podgląd pól:]]]') }}</h6>
                    <ul class="list-group list-group-flush">
                        <li v-for="component in result.formData.components" :key="component.aiName" class="list-group-item d-flex justify-content-between align-items-start">
                            <div class="ms-2 me-auto">
                                <div class="fw-bold">{{ component.aiLabel }}</div>
                                <small class="text-muted">
                                    {{ $t('[[[Typ:]]]') }} {{ getComponentTypeLabel(component) }}
                                    <span v-if="component.aiRequired" class="badge bg-warning ms-1">{{ $t('[[[wymagane]]]') }}</span>
                                </small>
                                <div v-if="component.aiPlaceholder" class="small text-muted mt-1">
                                    <em>{{ $t('[[[Placeholder:]]]') }} "{{ component.aiPlaceholder }}"</em>
                                </div>
                                <div v-if="component.aiOptions && component.aiOptions.length > 0" class="small text-muted mt-1">
                                    <strong>{{ $t('[[[Opcje:]]]') }}</strong> {{ component.aiOptions.join(', ') }}
                                </div>
                            </div>
                        </li>
                    </ul>
                    <!-- Przycisk do dodania komponentów do formularza -->
                    <div class="mt-3">
                        <button
                            type="button"
                            class="btn btn-success btn-sm"
                            @click="addComponentsToForm(result.formData.components)"
                        >
                            <i class="fa fa-plus me-1"></i>
                            {{ $t('[[[Dodaj pola do formularza]]]') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="result && !result.success" class="alert alert-warning mt-3" role="alert">
            {{ result.error || result.message || $t('[[[Nie udało się wygenerować formularza]]]') }}
        </div>
    </div>
</template>

<style lang="scss" scoped>
.ai-form-generator {
    border: 1px solid var(--bs-border-color);
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: var(--bs-light);
    margin-bottom: 1rem;

    h5 {
        color: var(--bs-primary);
        margin-bottom: 1rem;
        display: flex;
        align-items: center;

        &::before {
            content: "\e3b8"; // fa-message-bot / ai icon
            font-family: "Font Awesome 6 Pro";
            font-weight: 400;
            margin-right: 0.5rem;
        }
    }
}
</style>
